import { AdministrationFeesValidation } from "utils/validators/AdministrationFeesValidation";
import { AmountValidation } from "utils/validators/AmountValidation";
import { LenderMonthlyInterestValidation } from "utils/validators/LenderMonthlyInterestValidation";
import { PaybackTimeMonthsValidation } from "utils/validators/PaybackTimeMonthsValidation";
import { SetupFeeValidation } from "utils/validators/SetupFeeValidation";
import * as yup from "yup";

import type { Currency } from "@krea/shared/types/common";

import {
  BidTypeEnum,
  BuildingCreditPaymentType,
  PaymentFrequency,
} from "@/features/common/enums";
import type { IBuildingCreditBidInput } from "@/features/lender-responses/types/lender-response";

export const buildingCreditBidSchema: (
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) => yup.ObjectSchema<IBuildingCreditBidInput> = (
  currency,
  formatWithCurrency,
) =>
  yup
    .object()
    .shape({
      type: yup
        .string()
        .required("Type is required")
        .oneOf([BidTypeEnum.BUILDING_CREDIT])
        .default(BidTypeEnum.BUILDING_CREDIT),
      amount: AmountValidation(currency, formatWithCurrency),
      paybackTimeMonths: PaybackTimeMonthsValidation(),
      lenderMonthlyInterest: LenderMonthlyInterestValidation({
        isFunction: () => true,
      }).required(),
      liftFeePercentage: yup
        .number()
        .nullable()
        .typeError("Lift fee (%) is required")
        .min(0, (schema) => `Lift fee (%) can't be lower than ${schema.min}%`)
        .max(
          100,
          (schema) => `Lift fee (%) can't be greater than ${schema.max}%`,
        )
        .when("paymentType", ([paymentType], schema) =>
          paymentType === BuildingCreditPaymentType.EACH_LIFT
            ? schema.required("Lift fee (%) is required")
            : schema,
        ),
      paymentType: yup
        .string()
        .oneOf(Object.values(BuildingCreditPaymentType))
        .required("Payment type is required"),
      paymentFrequency: yup
        .string()
        .oneOf(Object.values(PaymentFrequency))
        .required("Payment frequency is required"),
      maxFinancingLtv: yup
        .number()
        .nullable()
        .optional()
        .min(0, "Must be >= 0")
        .max(100, "Must be <= 100"),
      maxFinancingLtc: yup
        .number()
        .nullable()
        .optional()
        .min(0, "Must be >= 0")
        .max(100, "Must be <= 100"),
      limitFeePerYearPercent: yup
        .number()
        .nullable()
        .min(0, "Limit fee per year can't be lower than 0")
        .max(100, "Limit fee per year can't be greater than 100%"),
      setupFee: SetupFeeValidation(formatWithCurrency).when(
        "paymentType",
        ([paymentType], schema) =>
          paymentType === BuildingCreditPaymentType.PAID_UPFRONT
            ? schema.required("Setup fee is required")
            : schema,
      ),
      administrationFees: AdministrationFeesValidation(formatWithCurrency, {
        boundByApprovedAmount: true,
      }),
    })
    .test(
      "financing-ltv-ltc-exactly-one",
      "Exactly one of max financing LTV or max financing LTC must be provided",
      function (values) {
        const hasLtv = values?.maxFinancingLtv != null;
        const hasLtc = values?.maxFinancingLtc != null;
        if (hasLtv === hasLtc) {
          const field = !hasLtv ? "maxFinancingLtv" : "maxFinancingLtc";
          return this.createError({
            // createError takes the path as-is, so it must be prefixed with
            // this bid's own path (bids[i]) to reach the field's error slot.
            path: this.path ? `${this.path}.${field}` : field,
            message: hasLtv
              ? "Provide only one: either max financing LTV or max financing LTC"
              : "Provide either a max financing LTV or a max financing LTC",
          });
        }
        return true;
      },
    );

export const buildingCreditBidDefaultValues: IBuildingCreditBidInput = {
  type: BidTypeEnum.BUILDING_CREDIT,
  amount: undefined,
  paybackTimeMonths: undefined,
  lenderMonthlyInterest: undefined,
  liftFeePercentage: undefined,
  paymentType: BuildingCreditPaymentType.PAID_UPFRONT,
  paymentFrequency: PaymentFrequency.MONTHLY,
  maxFinancingLtv: undefined,
  maxFinancingLtc: undefined,
  limitFeePerYearPercent: undefined,
  setupFee: undefined,
  administrationFees: undefined,
};
